/**
 * Branded PDF export for the relocation checklist (NextStepsPage).
 *
 * Uses @react-pdf/renderer — ships its own layout engine, so the output
 * is identical in any browser and doesn't depend on a browser print
 * stylesheet. The user gets a real PDF (`%PDF-` header), not a screenshot
 * of the HTML page.
 *
 * The shape of `ChecklistPdfData` deliberately matches the `NormalizedPhase`
 * shape already used by NextStepsPage so the page can hand its in-memory
 * phases straight to this component without re-shaping.
 *
 * Bilingual: supports both Latin and Cyrillic text via the NotoSans font
 * family (registered from /fonts/ served by the same origin). Built-in
 * Helvetica/Courier fonts only cover Latin-1 and will crash on Cyrillic.
 *
 * Emoji: flag emoji props (🇪🇸, 🇮🇹 …) are accepted but not rendered — standard
 * PDF fonts have no emoji glyphs and PDFKit throws on out-of-range codepoints.
 */

import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Svg,
  Path,
  Circle,
  Font,
} from '@react-pdf/renderer';

// ─── Font registration ────────────────────────────────────────────────────────
// Noto Sans covers Latin + Cyrillic (and much more).
// Files live in /public/fonts/ — served same-origin so CSP connect-src 'self'
// allows the fetch that @react-pdf/renderer makes when laying out text.
// We register at module-load time (once per bundle lifetime) and @react-pdf
// caches the loaded buffers internally, so repeated renders don't re-fetch.
Font.register({
  family: 'NotoSans',
  fonts: [
    { src: '/fonts/NotoSans-Regular.ttf', fontWeight: 400, fontStyle: 'normal' },
    { src: '/fonts/NotoSans-Bold.ttf', fontWeight: 700, fontStyle: 'normal' },
    { src: '/fonts/NotoSans-Italic.ttf', fontWeight: 400, fontStyle: 'italic' },
    { src: '/fonts/NotoSans-BoldItalic.ttf', fontWeight: 700, fontStyle: 'italic' },
  ],
});

// ─── Brand tokens (must match index.css token values) ────────────────────────
// Hard-coded here because @react-pdf/renderer styles cannot resolve CSS
// custom properties — it's a separate renderer with its own paint pipeline.
const COLOR = {
  paper: '#FAF7F0',
  ink900: '#1C1A16',
  ink800: '#2E2A22',
  ink700: '#4A453B',
  ink500: '#7A7368',
  ink400: '#9E978B',
  ink300: '#C0BAB0',
  ink100: '#E6E1D7',
  ink50: '#F0EBE0',
  terra600: '#1e6660', // matches --color-terra-600 in web/src/index.css
  terra700: '#174e4a',
  moss600: '#3F7A4E',
} as const;

const styles = StyleSheet.create({
  page: {
    paddingTop: 48,
    paddingBottom: 56, // room for footer
    paddingHorizontal: 48,
    backgroundColor: COLOR.paper,
    color: COLOR.ink900,
    fontFamily: 'NotoSans',
    fontSize: 10.5,
    lineHeight: 1.45,
  },
  // ── Header ─────────────────────────────────────────────────────────────────
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1pt solid ${COLOR.ink100}`,
    paddingBottom: 12,
    marginBottom: 18,
  },
  brandRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  brandText: {
    fontFamily: 'NotoSans',
    fontWeight: 700,
    fontSize: 14,
    color: COLOR.ink900,
    marginLeft: 8,
    letterSpacing: -0.2,
  },
  headerMeta: {
    fontFamily: 'NotoSans',
    fontSize: 8,
    color: COLOR.ink500,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  // ── Title block ────────────────────────────────────────────────────────────
  titleBlock: { marginBottom: 10 },
  title: {
    fontFamily: 'NotoSans',
    fontWeight: 700,
    fontStyle: 'italic',
    fontSize: 22,
    color: COLOR.ink900,
    marginBottom: 6,
  },
  subtitle: {
    fontFamily: 'NotoSans',
    fontSize: 10,
    color: COLOR.ink500,
    letterSpacing: 0.3,
    marginBottom: 5,
  },
  context: {
    fontFamily: 'NotoSans',
    fontSize: 8.5,
    color: COLOR.ink400,
    marginBottom: 18,
  },
  // ── Phase section ──────────────────────────────────────────────────────────
  phaseSection: {
    marginTop: 14,
    border: `0.6pt solid ${COLOR.ink100}`,
    borderRadius: 4,
    padding: 12,
    backgroundColor: '#FFFFFF',
  },
  phaseHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 10,
    paddingBottom: 6,
    borderBottom: `0.4pt solid ${COLOR.ink50}`,
  },
  phaseTitle: {
    fontFamily: 'NotoSans',
    fontWeight: 700,
    fontStyle: 'italic',
    fontSize: 13,
    color: COLOR.ink900,
  },
  phaseTimeline: {
    fontFamily: 'NotoSans',
    fontSize: 8,
    color: COLOR.ink400,
    letterSpacing: 0.5,
  },
  phaseAccentBar: {
    width: 28,
    height: 2,
    backgroundColor: COLOR.terra600,
    marginBottom: 8,
  },
  // ── Items ──────────────────────────────────────────────────────────────────
  itemRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  checkbox: {
    width: 10,
    height: 10,
    marginTop: 1,
    marginRight: 8,
  },
  itemText: {
    flex: 1,
    fontFamily: 'NotoSans',
    fontSize: 10.5,
    color: COLOR.ink800,
  },
  itemTextDone: {
    flex: 1,
    fontFamily: 'NotoSans',
    fontSize: 10.5,
    color: COLOR.ink400,
    textDecoration: 'line-through',
  },
  itemDetail: {
    marginLeft: 18,
    marginBottom: 8,
    fontFamily: 'NotoSans',
    fontSize: 9,
    color: COLOR.ink500,
    lineHeight: 1.4,
  },
  // ── Resources ──────────────────────────────────────────────────────────────
  resourcesBlock: {
    marginTop: 10,
    paddingTop: 8,
    borderTop: `0.4pt solid ${COLOR.ink50}`,
  },
  resourcesLabel: {
    fontFamily: 'NotoSans',
    fontWeight: 700,
    fontSize: 7.5,
    color: COLOR.ink400,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  resourceLine: {
    fontFamily: 'NotoSans',
    fontSize: 9,
    color: COLOR.terra700,
    marginBottom: 2,
  },
  resourceNote: {
    color: COLOR.ink500,
  },
  // ── Disclaimer ─────────────────────────────────────────────────────────────
  disclaimer: {
    marginTop: 24,
    paddingTop: 10,
    borderTop: `0.4pt solid ${COLOR.ink100}`,
    fontFamily: 'NotoSans',
    fontStyle: 'italic',
    fontSize: 8,
    color: COLOR.ink500,
    lineHeight: 1.5,
  },
  // ── Footer (rendered absolute on every page) ──────────────────────────────
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 48,
    right: 48,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 8,
    borderTop: `0.4pt solid ${COLOR.ink100}`,
  },
  footerText: {
    fontFamily: 'NotoSans',
    fontSize: 7.5,
    color: COLOR.ink400,
    letterSpacing: 0.6,
  },
});

// ─── Component-level types ───────────────────────────────────────────────────

/** Shape that matches NormalizedPhase from NextStepsPage. */
export interface ChecklistPdfPhase {
  id: string;
  title: string;
  timeline: string;
  items: Array<{ id: string; label: string; detail: string; done?: boolean }>;
  resources: Array<{ label: string; url: string; note?: string }>;
}

export interface ChecklistPdfStrings {
  documentTitle: string;     // "Moving to Italy — next steps"
  subtitle: string;          // "A practical checklist…"
  generatedFor: string;      // "Generated for {email|anonymous} on {date}"
  resourcesLabel: string;    // "Resources"
  disclaimer: string;
  footerUrl: string;         // "lodestar.place"
  pageNumber: (page: number, total: number) => string;
}

export interface ChecklistPdfProps {
  /** Country flag emoji, e.g. "🇮🇹". Optional — stripped before rendering
   *  (emoji codepoints are outside the BMP and crash PDFKit/fontkit). */
  flag?: string | null;
  /** Country display name, e.g. "Italy". */
  countryName: string;
  /** Pre-translated phase data — straight from NextStepsPage's normalize step. */
  phases: ChecklistPdfPhase[];
  /** All copy strings, pre-translated by the caller using react-i18next. */
  strings: ChecklistPdfStrings;
}

// ─── Compass-mark SVG (mirrors LodestarMark.tsx, terra accent) ──────────────
function LodestarPdfMark({ size = 18 }: { size?: number }) {
  // Re-encoded in @react-pdf/renderer's <Svg> primitives (cannot reuse the
  // React-DOM <svg> from src/components/brand because that uses HTML SVG).
  return (
    <Svg width={size} height={size} viewBox="0 0 64 64">
      <Circle cx="32" cy="32" r="30" fill={COLOR.ink900} />
      <Path d="M10 32 L32 35 L54 32 L32 29 Z" fill={COLOR.paper} />
      <Path d="M32 54 L36 32 L28 32 Z" fill={COLOR.paper} />
      <Path d="M32 6 L37 32 L27 32 Z" fill={COLOR.terra600} />
      <Circle cx="32" cy="32" r="2.5" fill={COLOR.ink900} />
    </Svg>
  );
}

// ─── Checkbox glyph (drawn so we don't depend on a Unicode tick font) ───────
function CheckboxGlyph({ done }: { done: boolean }) {
  return (
    <Svg style={styles.checkbox} viewBox="0 0 12 12">
      <Path
        d="M1 1 L11 1 L11 11 L1 11 Z"
        fill="#FFFFFF"
        stroke={done ? COLOR.terra600 : COLOR.ink400}
        strokeWidth={1}
      />
      {done && (
        // A small tick stroke inside the box for the "done" state.
        <Path
          d="M3 6.5 L5.2 8.8 L9 4.2"
          fill="none"
          stroke={COLOR.terra600}
          strokeWidth={1.4}
        />
      )}
    </Svg>
  );
}

// ─── Main PDF document ──────────────────────────────────────────────────────
export function ChecklistPdf({
  countryName,
  phases,
  strings,
}: ChecklistPdfProps) {
  return (
    <Document
      title={strings.documentTitle}
      author="Lodestar"
      creator="lodestar.place"
      producer="lodestar.place"
    >
      <Page size="A4" style={styles.page} wrap>
        {/* Header */}
        <View style={styles.header} fixed>
          <View style={styles.brandRow}>
            <LodestarPdfMark size={18} />
            <Text style={styles.brandText}>Lodestar</Text>
          </View>
          <Text style={styles.headerMeta}>{strings.footerUrl}</Text>
        </View>

        {/* Title block */}
        <View style={styles.titleBlock}>
          <Text style={styles.title}>{countryName}</Text>
          <Text style={styles.subtitle}>{strings.documentTitle}</Text>
          <Text style={styles.context}>{strings.generatedFor}</Text>
        </View>

        {/* Phases */}
        {phases.map((phase) => (
          <View key={phase.id} style={styles.phaseSection} wrap={false}>
            <View style={styles.phaseAccentBar} />
            <View style={styles.phaseHeader}>
              <Text style={styles.phaseTitle}>{phase.title}</Text>
              {phase.timeline ? (
                <Text style={styles.phaseTimeline}>{phase.timeline}</Text>
              ) : null}
            </View>

            {phase.items.map((item) => (
              <View key={item.id}>
                <View style={styles.itemRow}>
                  <CheckboxGlyph done={!!item.done} />
                  <Text style={item.done ? styles.itemTextDone : styles.itemText}>
                    {item.label}
                  </Text>
                </View>
                {item.detail ? (
                  <Text style={styles.itemDetail}>{item.detail}</Text>
                ) : null}
              </View>
            ))}

            {phase.resources.length > 0 && (
              <View style={styles.resourcesBlock}>
                <Text style={styles.resourcesLabel}>{strings.resourcesLabel}</Text>
                {phase.resources.map((r) => (
                  <Text key={r.url} style={styles.resourceLine}>
                    {`• ${r.label} — ${r.url}`}
                    {r.note ? (
                      <Text style={styles.resourceNote}> · {r.note}</Text>
                    ) : null}
                  </Text>
                ))}
              </View>
            )}
          </View>
        ))}

        {/* Disclaimer */}
        <Text style={styles.disclaimer}>{strings.disclaimer}</Text>

        {/* Footer (page numbers — render() callback is fired by react-pdf
             per page so this Text component is the source of truth for ALL
             pages, not just page 1). */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>{strings.footerUrl}</Text>
          <Text
            style={styles.footerText}
            render={({ pageNumber, totalPages }) =>
              strings.pageNumber(pageNumber, totalPages)
            }
          />
        </View>
      </Page>
    </Document>
  );
}
